<template>
    <div class="input-field-wrapper">
        <input
            class="input-field"
            :maxlength="maxlength"
            :value="!focused && numeric ? formattedInputValue : value"
            v-bind="$attrs"
            @blur="onBlur"
            @focus="onFocus"
            @input="onInput"
        />

        <span
            class="input-field-wrapper__legend"
            :class="{
                'input-field-wrapper__legend--negative': value.length >= maxlengthNumber,
            }"
            v-if="maxlength && typeof value === 'string'"
            v-show="focused"
        >
            <span>{{ value.length }}</span
            >/<span>{{ maxlengthNumber }}</span>
        </span>
    </div>
</template>

<script lang="ts">
// Svg
// ...

// Components
// ...

// Other
import { sanitizeNumericInput } from '@/utils/input-utils';
import { debounce } from 'debounce';
import { defineComponent, markRaw, PropType } from 'vue';

type DebounceFunction = ((value: string | number) => void) & { clear(): void } & { flush(): void };

export default defineComponent({
    inheritAttrs: false,

    props: {
        numeric: { type: Boolean, default: false },
        maxlength: { type: [Number, String], default: undefined },
        modelValue: { type: [String, Number, null] as PropType<string | number | null>, required: true, default: '' },
        debounceMode: { type: Boolean, default: false },
        debounceInterval: { type: Number, default: 500 },
        concurrencyMode: { type: Boolean, default: false },
        roundDecimals: { type: Boolean, default: false },
        formatPercent: { type: Boolean, default: false },
    },

    data: () => ({
        value: '' as string | number | null,
        focused: false,
        debounce: null as DebounceFunction | null,
    }),

    methods: {
        onFocus() {
            this.focused = true;
        },

        onBlur() {
            this.focused = false;

            if (this.numeric && this.value) {
                this.value = parseFloat(sanitizeNumericInput(this.value.toString()));
            }
        },

        onInput(event: Event) {
            const input = event.target as HTMLInputElement;
            let inputValue = input.value;

            if (this.numeric) {
                var numericValue = sanitizeNumericInput(inputValue);

                inputValue = numericValue;
                input.value = numericValue;
            } else if (this.maxlengthNumber && inputValue.length > this.maxlengthNumber) {
                // Fallback if the `maxlength` attribute does not work.
                input.value = inputValue.substring(0, this.maxlengthNumber);
                return;
            }

            this.value = inputValue;

            const emitValue = (newValue: string | number) => {
                if (this.numeric && typeof newValue === 'string') {
                    // Process numeric input
                    const numericValue = parseFloat(sanitizeNumericInput(newValue));
                    if (isNaN(numericValue)) {
                        return;
                    }
                    newValue = numericValue;
                }

                if (newValue === this.modelValue) {
                    return;
                }

                this.$emit('update:modelValue', newValue);
                this.debounce = null;
            };

            if (this.debounceMode && !this.debounce) {
                this.debounce = markRaw(debounce(emitValue, this.debounceInterval));
            }

            if (this.debounce) {
                this.debounce(inputValue);
                return;
            }

            emitValue(inputValue);
        },
    },

    computed: {
        maxlengthNumber(): number {
            return parseInt(this.maxlength as string, 10) || 0;
        },

        formattedInputValue(): string | number {
            if (this.value === null || this.value === undefined || this.value === '') {
                return '';
            }

            const numericValue = parseFloat(this.value.toString());
            if (isNaN(numericValue) || !this.numeric) {
                return this.value;
            }

            const ruLocale = 'ru-RU';
            const formatOptions: Intl.NumberFormatOptions = {
                minimumFractionDigits: 0,
                maximumFractionDigits: this.roundDecimals ? 2 : 20,
                style: this.formatPercent ? 'percent' : 'decimal',
            };

            return new Intl.NumberFormat(ruLocale, formatOptions).format(numericValue);
        },
    },

    watch: {
        modelValue(value: string) {
            // If the user is in the input window, ignore all changes.
            if (this.concurrencyMode && this.focused) {
                return;
            }

            this.value = value;
        },
    },

    created() {
        this.value = this.modelValue;
    },
});
</script>

<style lang="scss">
.input-field {
    margin: 0;
    padding: 0;
    border: none;
    outline: none;
    background-color: var(--background-color);
    --background-color: inherit;
    color: var(--text-black-primary);
    text-overflow: ellipsis;

    &--primary {
        @include input-primary(0.75rem);
    }

    &--bordered {
        border-color: var(--text-black-tertiary);
    }

    &--inline-50 {
        @include input-primary-width(0.5rem);
    }

    &--inline-75 {
        @include input-primary-width(0.75rem);
    }

    &--inline-100 {
        @include input-primary-width(1rem);
    }

    &--text-center {
        text-align: center;
    }

    &.error {
        border-color: var(--color-negative);
    }
}

.input-field-wrapper {
    position: relative;
    width: 100%;

    &__legend {
        position: absolute;
        padding: 0 0.25rem;
        right: 1.75rem;
        bottom: -0.5rem;
        background: var(--background-color);

        @include caption-tertiary();

        & span:first-child {
            color: var(--brand-green);
        }

        &--negative span {
            color: var(--color-negative) !important;
        }
    }
}
</style>
